import { DOCUMENT, NgClass, NgStyle } from '@angular/common';
import { Component, Inject, Input } from '@angular/core';
import { RouterModule } from '@angular/router';
import { StoreSpecificConfigService } from '@scalefast/config-angular';
import { CATEGORY_FILTERS_OPTIONS, Product } from '@scalefast/core';
import { ProductsDisplayedAction } from '@scalefast/ecommerce-core';
import { CustomBuilderComponent } from 'src/app/core/decorators';
import {
  ComparisonProductVariant,
  ContentfulComparisonProduct,
} from 'src/app/core/interfaces/Contentful/Comparison/ContentfulComparisonProduct';

import { ExtractBazaarvoiceID } from 'src/app/shared/pipes/extract-bazaarvoice-id.pipe';
import { CustomStoreSearchService } from 'src/app/shared/services/store-search.service';
import { AppProductPriceComponent } from '../../../../components/product/product-price/product-price.component';
import { AppBazaarvoiceComponent } from '../../bazaarvoice/bazaarvoice.component';

export const comparisonComponentConfig = {
  tag: 'app-cc-comparison',
  name: 'comparison between series',
  inputs: [
    {
      name: 'series',
      type: 'list',
      subFields: [
        {
          name: 'object_id',
          type: 'string',
        },
        {
          name: 'product_id',
          type: 'string',
        },
        {
          name: 'showCta',
          type: 'boolean',
          helperText: 'This will enable the button of Purchase now that drives the user to PDP',
          defaultValue: true,
        },
      ],
    },
    {
      name: 'title',
      type: 'string',
    },
  ],
};

@Component({
  standalone: true,
  selector: 'app-cc-comparison',
  templateUrl: './comparison.component.html',
  styleUrls: ['./comparison.styles.scss'],
  imports: [NgClass, NgStyle, RouterModule, AppBazaarvoiceComponent, ExtractBazaarvoiceID, AppProductPriceComponent],
})
@CustomBuilderComponent(comparisonComponentConfig)
export class AppComparisonComponent {
  data: {
    fields: any;
    id: string;
  }[] = [];

  comparisonProducts: ContentfulComparisonProduct[] = [];

  showFromPriceText: boolean;
  showPrices: boolean;

  @Input()
  set builderState(d: any) {
    this.rebuildData(d);
  }

  @Input()
  set series(builderData: any) {
    this.extractProductsData(builderData);
    this.getProductsESWInfo();
  }

  @Input() title: any;

  constructor(
    @Inject(DOCUMENT) private _doc: Document,
    private filterService: CustomStoreSearchService,
    private storeSpecificConfigService: StoreSpecificConfigService,
  ) {
    let specificConfig = this.storeSpecificConfigService.get();
    this.showFromPriceText = specificConfig.showFromInMetaProductPrices;
    this.showPrices = specificConfig.showPricesOnComparisonProducts;
  }

  rebuildData(d: any) {
    if (d?.content?.data?.state) {
      this.data = Object.keys(d.content.data.state).map((key) => d.content.data.state[key]);
    }
  }

  extractProductsData(builderData: any): void {
    this.comparisonProducts = [];
    if (!Array.isArray(builderData) || builderData.length === 0) {
      return;
    }
    builderData.forEach((builderElement: { object_id: string; product_id?: string; showCta: boolean }) => {
      if (builderElement.object_id === null) {
        return;
      }
      // GET MATCHING PRODUCT FROM CONTENTFUl DATA
      const contentfulProduct = this.data.find((data) => data.id == builderElement.object_id);

      if (!contentfulProduct) {
        return;
      }

      // EXTRACT CONTENTFUL DATA FROM THE MATCHED PRODUCT
      const deactivateBuyNowButton: boolean = contentfulProduct.fields.deactivateBuyNowButton ?? false;
      const shortTitle: string =
        contentfulProduct.fields.shortTitle ?? contentfulProduct.fields?.productVariants?.[0]?.fields?.shortTitle;
      const buyNowLabel: string = contentfulProduct.fields.buyNowLabel.fields.text ?? '';
      const options: string[] = contentfulProduct.fields?.options
        ? contentfulProduct.fields.options.replaceAll('- ', '').split('\n')
        : [];
      const productVariants: ComparisonProductVariant[] = contentfulProduct.fields.productVariants.map((productVariant: any) => {
        const productVariantElement: ComparisonProductVariant = {
          bazaarvoiceId: productVariant?.fields?.bazaarVoiceId,
          ean: productVariant?.fields?.ean,
          isBazaarvoiceIdentifier: productVariant?.fields?.isBazaarvoiceIdenfier,
          mainAsset: productVariant?.fields?.mainAsset,
        };
        if (productVariant?.fields?.color) {
          const productVariantColor = productVariant.fields.color;
          productVariantElement.color = {
            colorCode: productVariantColor.fields?.colorCode,
            colorCodeBottom: productVariantColor.fields?.colorCodeBottom,
            colorCodeTop: productVariantColor.fields?.colorCodeTop,
            gradientStyle: productVariantColor.fields?.gradientStyle,
            colorImage: productVariantColor.fields?.colorImage?.fields?.file?.url,
            name: productVariantColor.fields?.name,
          };
        }
        return productVariantElement;
      });

      const comparisonProduct: ContentfulComparisonProduct = {
        id: builderElement.object_id,
        product_id: builderElement?.product_id,
        shortTitle: shortTitle,
        buyNowLabel: buyNowLabel,
        deactivateBuyNowButton: deactivateBuyNowButton,
        options: options,
        mainAsset: contentfulProduct.fields.mainAsset,
        productVariants: productVariants,
        showPurchaseButton: builderElement.showCta,
      };

      if (this.isValidComparisonProduct(comparisonProduct)) {
        this.comparisonProducts.push(comparisonProduct);
      }
    });
  }

  isValidComparisonProduct(comparisonProduct: ContentfulComparisonProduct): boolean {
    let isValid = true;
    if (!comparisonProduct.shortTitle || !comparisonProduct.buyNowLabel || !comparisonProduct.options.length) {
      isValid = false;
    }
    return isValid;
  }

  getProductsESWInfo(): void {
    //Builder triggers data setting multiple times on component lifecycle. With this we avoid making more requests than necessary.
    const searchProducts: ContentfulComparisonProduct[] = this.comparisonProducts.filter(
      (comparisonProduct) => !comparisonProduct?.productESWInfo,
    );

    if (!this.showPrices || searchProducts.length === 0) {
      return;
    }

    const action: ProductsDisplayedAction = this.filterService.setEventProductDisplayedAction({
      offset: CATEGORY_FILTERS_OPTIONS.DEFAULT_PAGE_OFFSET,
      pageSize: CATEGORY_FILTERS_OPTIONS.DEFAULT_PAGE_SIZE,
      filters: [],
    });
    const filterParams: any = {
      ...this.filterService.getStoreSearchParams(),
      count: searchProducts.length,
      offset: 0,
      filters: searchProducts.map((comparisonProduct) => `productID:${comparisonProduct.product_id}`).join('*'),
      crit: 'ALL',
    };
    this.filterService.getProductsByFilters(filterParams, action).subscribe((data: any) => {
      if (!data.products) {
        return;
      }
      this.comparisonProducts.forEach((comparisonProduct) => {
        const productESWInfo: Product = data.products.find((product: any) => product.id === comparisonProduct.product_id);
        if (productESWInfo) {
          comparisonProduct.productESWInfo = productESWInfo;
        }
      });
    });
  }

  formatColorOptions(option: string = '') {
    return option.replaceAll('-', ' ');
  }

  getGradient(colors: any) {
    if (!colors) {
      return {
        background: `#ff`,
      };
    }

    const types: any = {
      highlightTop: 'to top',
      highlightBottom: 'to bottom',
      circle: 'circle at center top',
    };

    if (colors.colorImage) {
      return {
        'background-image': `url('${colors.colorImage}')`,
        'background-size': 'cover',
        width: '45px',
        height: '45px',
      };
    }

    return {
      background: `linear-gradient(${types[colors.gradientStyle] ?? 'to top'}, ${
        colors.colorCodeBottom
      } 0%, ${colors.colorCodeTop} 100%)`,
    };
  }

  changeImage(index: number, variant: any, variantIndex: number, id: string) {
    const imgElement = this._doc.querySelector(`#img_${index}`) as HTMLImageElement;
    imgElement.src = variant.mainAsset.fields.asset.fields.file.url;

    let options = this._doc.getElementById('comparison__selector-' + id);
    if (options) {
      let btns = options.getElementsByClassName('comparison__sphere-color');
      for (let index = 0; index < btns.length; index++) {
        btns[index].classList.remove('active');
      }
      btns[variantIndex].classList.add('active');
    }
  }

  getOptions(options: string): string {
    const regex = /__(.*?)__/g;
    return options.replace(regex, '<strong>$1</strong>');
  }
}
